<template>
	<!-- The deal's facts, as investor-app's Investment Details card. -->
	<div class="side-card">
		<h3 class="side-title">Investment Details</h3>
		<!-- What matters differs by deal: a metal says what it is and what its price follows, an
		     NGX stock where it trades, a fund who manages it and how it is priced. -->
		<div class="details-list">
			<div v-for="row in rows" :key="row.label" class="detail-row" :class="{ highlight: row.highlight }">
				<span class="detail-label">{{ row.label }}</span>
				<span class="detail-value">
					<span v-if="row.badge === 'type'" class="type-badge">{{ row.value }}</span>
					<span v-else-if="row.badge === 'risk'" class="risk-badge" :class="String(row.value).toLowerCase()">{{ row.value }}</span>
					<template v-else>{{ row.value }}</template>
				</span>
			</div>
		</div>
		<div class="provider-row">
			<span class="provider-label">Provided by</span>
			<a
				:href="isCrowdfundCompany ? 'https://getequity.io' : token.creator?.website"
				target="_blank"
				rel="noopener noreferrer"
				class="provider-link"
			>
				{{ isCrowdfundCompany ? "GetEquity" : token.creator?.name }}
				<svg viewBox="0 0 16 16" fill="none"><path d="M6 3.333H4.667A1.333 1.333 0 003.333 4.667v6.666a1.333 1.333 0 001.334 1.334h6.666a1.333 1.333 0 001.334-1.334V10M9.333 3.333h3.334M12.667 3.333v3.334M6.667 9.333l6-6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
			</a>
		</div>
	</div>
</template>

<script setup>
import { formatAmountToDollar, formatToStringDate } from "@/utils/helpers";
import { OFFERING_TYPE_LABELS } from "@/utils/offering";
import { DealKinds, dealKind, priceDecimals, unitPrice, unitWord } from "@/utils/trade-view";
import { computed } from "vue";
const props = defineProps({
	token: {
		type: Object,
		required: true,
	},
	asset: {
		type: Object,
		required: true,
	},
	// The metal details on a commodity deal, from CommodityInfo.
	commodity: {
		type: Object,
		default: null,
	},
	// The NGX listing, when this deal follows one. It is what makes a deal an NGX stock.
	ngx: {
		type: Object,
		default: null,
	},
	// The page's figure cards already show the shares on offer.
	hideSharesOnOffer: {
		type: Boolean,
		default: false,
	},
});

const token = computed(() => props.token);

const kind = computed(() => dealKind(token.value, { ngx: props.ngx }));

const currencyName = computed(() => {
	const symbol = token.value.currency;
	let currency = null;
	switch (symbol) {
		case "NGN":
			currency = "Nigerian Naira";
			break;
		case "USD":
			currency = "United States Dollar";
			break;
		case "KES":
			currency = "Kenyan Shillings";
			break;
		default:
			currency = "United States Dollar";
			break;
	}
	return currency;
});

const isCrowdfundCompany = computed(() => {
	return token.value.creator?.type === "Crowdfund Company";
});

// The first rows: what the metal is, where the stock trades, who runs the fund, or the industry.
const leadRows = computed(() => {
	const t = token.value;
	if (kind.value === DealKinds.COMMODITY) {
		const group = props.commodity?.kind;
		return [{ label: group && group !== "metal" ? "Commodity" : "Metal", value: props.commodity?.name || t.name }];
	}
	if (kind.value === DealKinds.LISTED) {
		const sector = props.ngx?.sector || props.asset?.industry;
		return [
			{ label: "Exchange", value: "Nigerian Exchange (NGX)" },
			props.ngx?.symbol && { label: "Ticker", value: props.ngx.symbol },
			sector && { label: "Sector", value: sector },
		].filter(Boolean);
	}
	if (kind.value === DealKinds.FUND) return t.creator?.name ? [{ label: "Manager", value: t.creator.name }] : [];
	return props.asset?.industry ? [{ label: "Industry", value: props.asset.industry }] : [];
});

// What trading costs, where it is the main cost of holding: funds, metals and stocks. A company
// pays half the deal's fee each way, as the backend charges it (exchange-process
// getBuyTransactionFee / getSellTransactionFee), so that is the figure shown here and in the drawer.
const feesText = computed(() => {
	const t = token.value;
	if (![DealKinds.FUND, DealKinds.COMMODITY, DealKinds.LISTED].includes(kind.value)) return "";
	if (t.buy_fee == null && t.sell_fee == null) return "";
	const half = (fee) => Number(((Number(fee) || 0) / 2).toFixed(4));
	const buy = half(t.buy_fee);
	const sell = half(t.sell_fee);
	return buy || sell ? `${buy}% buying, ${sell}% selling` : "None";
});

const rows = computed(() => {
	const t = token.value;
	const currency = t.currency || "USD";
	const isFund = kind.value === DealKinds.FUND;
	const type = t.investment_type;
	const isNote = type === "Debt" || type === "Fixed Interest";
	// What one unit is and how its price is set, for deals bought by the unit at a price that
	// moves. A note's terms are its rate and maturity.
	const priceLabel = [DealKinds.DEBT, DealKinds.BIDDING].includes(kind.value)
		? ""
		: isFund ? "Unit Price" : `Price per ${unitWord(t, 1)}`;
	return [
		...leadRows.value,
		(isNote || (type === "Equity" && t.interest > 1)) && { label: "Interest Rate", value: `${t.interest}% per annum` },
		(isNote || type === "Equity") && t.tenor > 1 && { label: "Investment Period", value: `${t.tenor} days` },
		{ label: "Investment Type", value: kind.value === DealKinds.LISTED ? "Listed stock" : type, badge: "type" },
		isFund && t.investment_category && { label: "Category", value: t.investment_category },
		kind.value === DealKinds.BIDDING && OFFERING_TYPE_LABELS[t.offering_type] && { label: "Offer Type", value: OFFERING_TYPE_LABELS[t.offering_type] },
		t.risk && { label: "Risk Level", value: t.risk, badge: "risk" },
		t.sharia_compliant === true && { label: "Sharia Compliant", value: "Yes, as stated by the sponsor" },
		![DealKinds.COMMODITY, DealKinds.LISTED, DealKinds.FUND].includes(kind.value) && { label: "Country", value: t.country || "Nigeria" },
		priceLabel && { label: priceLabel, value: formatAmountToDollar(unitPrice(t), priceDecimals(t), currency) },
		kind.value === DealKinds.EQUITY && !t.completed_raise && t.supply > 0 && !props.hideSharesOnOffer && { label: "Shares on Offer", value: Number(t.supply).toLocaleString() },
		props.commodity && { label: "Each Token", value: props.commodity.token_is },
		props.commodity && { label: "Price Follows", value: `${props.commodity.name} ${props.commodity.price_basis === "front_month" ? "futures" : "spot"}, weekdays` },
		isFund && { label: "Pricing", value: t.interest > 0 ? `Daily, at ${t.interest}% a year` : "Net asset value" },
		(isNote || (type === "Equity" && t.payout_frequency)) && { label: "Payout Frequency", value: t.payout_frequency },
		isNote && t.tenor > 1 && { label: "Maturity Date", value: formatToStringDate(t?.maturity, true) },
		feesText.value && { label: "Fees", value: feesText.value },
		t.currency && { label: "Currency", value: currencyName.value },
		{ label: "Minimum Investment", value: formatAmountToDollar(t.min_trade?.buy, 2, currency), highlight: true },
	].filter(Boolean);
});
</script>

<style lang="scss" scoped>
$bg-card: #FFFFFF;
$border-color: #E2E8F0;
$text-primary: #0F172A;
$text-muted: #94A3B8;
$accent: #4D84FF;
$btn-primary: #001240;
$success: #10B981;
$warning: #F59E0B;
$error: #EF4444;

.side-card {
	background: $bg-card;
	border: 1px solid $border-color;
	border-radius: 10px;
	padding: 20px;
}
.side-title {
	font-size: 15px;
	font-weight: 400;
	color: $text-primary;
	margin: 0 0 16px;
	padding-bottom: 12px;
	border-bottom: 1px solid $border-color;
}
.details-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}
.detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	&.highlight {
		padding: 12px;
		margin: 8px -12px 0;
		background: rgba($btn-primary, 0.03);
		border-radius: 4px;
	}
}
.detail-label {
	font-size: 13px;
	color: $text-muted;
	flex-shrink: 0;
}
.detail-value {
	font-size: 13px;
	font-weight: 400;
	color: $text-primary;
	text-align: right;
}
.type-badge {
	padding: 3px 8px;
	background: rgba($btn-primary, 0.08);
	color: $btn-primary;
	border-radius: 4px;
	font-size: 12px;
}
.risk-badge {
	padding: 3px 8px;
	border-radius: 4px;
	font-size: 12px;
	&.low { background: rgba($success, 0.1); color: darken($success, 10%); }
	&.medium { background: rgba($warning, 0.1); color: darken($warning, 10%); }
	&.high { background: rgba($error, 0.1); color: $error; }
}
.provider-row {
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid $border-color;
}
.provider-label {
	display: block;
	font-size: 12px;
	color: $text-muted;
	margin-bottom: 4px;
}
.provider-link {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 13px;
	font-weight: 400;
	color: $accent;
	text-decoration: none;
	svg { width: 14px; height: 14px; }
	&:hover { text-decoration: underline; }
}
</style>
